/**
 * CRMIntermediariPage — Gestione intermediari (sub-agenti RUI + broker esterni).
 * TASK 3 / FASE 3.5, feb 2026.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Briefcase, Search, X, Trash2, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPI = [
  { v: "collaboratore_sub_agente", l: "Collaboratore sub-agente (RUI)" },
  { v: "broker_esterno", l: "Intermediario esterno" },
  { v: "agente_diretto_colucci", l: "Agente diretto Colucci" },
];

const empty = {
  codice: "", ragione_sociale: "", tipo: "collaboratore_sub_agente",
  rui_numero: "", rui_sezione: "E", email: "", telefono: "+39",
  pec: "", piva: "", cf: "", iban: "",
  default_provvigione_pct: "", attivo: true, note: "",
};

export default function CRMIntermediariPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (tipo) params.set("tipo", tipo);
      const { data } = await axios.get(`${API}/api/admin/crm/intermediari?${params.toString()}`, { withCredentials: true });
      setItems(data.items);
    } catch (e) {
      toast.error("Errore caricamento intermediari");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [q, tipo]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      codice: i.codice, ragione_sociale: i.ragione_sociale, tipo: i.tipo,
      rui_numero: i.rui_numero || "", rui_sezione: i.rui_sezione || "E",
      email: i.email, telefono: i.telefono, pec: i.pec || "",
      piva: i.piva || "", cf: i.cf || "", iban: i.iban || "",
      default_provvigione_pct: i.default_provvigione_pct ?? "",
      attivo: i.attivo, note: i.note || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.default_provvigione_pct === "") delete payload.default_provvigione_pct;
      else payload.default_provvigione_pct = Number(payload.default_provvigione_pct);
      // remove campi non valorizzati opzionali per evitare 422 su email validator
      ["pec", "piva", "cf", "iban", "rui_numero", "note"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (editing) {
        await axios.put(`${API}/api/admin/crm/intermediari/${editing.id}`, payload, { withCredentials: true });
        toast.success("Intermediario aggiornato");
      } else {
        await axios.post(`${API}/api/admin/crm/intermediari`, payload, { withCredentials: true });
        toast.success("Intermediario creato");
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail?.error_code || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Cancellare l'intermediario? Soft delete con possibilita' di rollback.")) return;
    try {
      await axios.delete(`${API}/api/admin/crm/intermediari/${id}`, { withCredentials: true });
      toast.success("Intermediario rimosso");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Errore");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-intermediari-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-[#163321]" />Intermediari
        </h1>
        <button onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#163321] hover:bg-[#0f2517] text-white px-4 py-2 rounded-lg"
          data-testid="btn-new-intermediario">
          <Plus className="w-4 h-4" />Nuovo intermediario
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, codice, email..."
            className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:border-[#163321] focus:outline-none"
            data-testid="search-intermediari" />
        </div>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-3 py-2 border border-stone-300 rounded-lg" data-testid="filter-tipo">
          <option value="">Tutti i tipi</option>
          {TIPI.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-12" /> : items.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 border border-dashed rounded-lg">
          <p className="text-stone-600">Nessun intermediario.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="intermediari-grid">
          {items.map((i) => (
            <div key={i.id} className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`intermediario-${i.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-stone-900 truncate">{i.ragione_sociale}</h3>
                  <div className="text-xs text-stone-500 font-mono">{i.codice}</div>
                </div>
                {!i.attivo && <span className="text-xs px-2 py-0.5 bg-stone-200 rounded-full">Inattivo</span>}
              </div>
              <div className="text-xs text-stone-600 mb-2">{TIPI.find(t => t.v === i.tipo)?.l || i.tipo}</div>
              {i.rui_numero && <div className="text-xs text-stone-500">RUI: {i.rui_sezione}/{i.rui_numero}</div>}
              <div className="text-xs text-stone-600 mt-2">{i.email}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(i)} className="text-xs text-[#163321] hover:underline" data-testid={`edit-${i.id}`}>Modifica</button>
                <button onClick={() => remove(i.id)} className="text-xs text-rose-600 hover:underline ml-auto">
                  <Trash2 className="w-3 h-3 inline" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="intermediario-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{editing ? "Modifica" : "Nuovo"} intermediario</h2>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-stone-700 mb-1">Ragione sociale *</label>
                <input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg" data-testid="field-ragione-sociale" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Codice *</label>
                <input value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })}
                  disabled={!!editing}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg font-mono uppercase disabled:bg-stone-100"
                  data-testid="field-codice" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Tipo *</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg" data-testid="field-tipo">
                  {TIPI.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              {form.tipo === "collaboratore_sub_agente" && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Numero RUI</label>
                    <input value={form.rui_numero} onChange={(e) => setForm({ ...form, rui_numero: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Sezione RUI</label>
                    <select value={form.rui_sezione} onChange={(e) => setForm({ ...form, rui_sezione: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg">
                      {"ABCDE".split("").map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg" data-testid="field-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Telefono *</label>
                <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg" data-testid="field-telefono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">P.IVA</label>
                <input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value.replace(/\D/g, "") })}
                  maxLength={11} className="w-full px-3 py-2 border border-stone-300 rounded-lg font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Provvigione default %</label>
                <input type="number" step="0.1" min="0" max="100" value={form.default_provvigione_pct}
                  onChange={(e) => setForm({ ...form, default_provvigione_pct: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-stone-300 rounded-lg">Annulla</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-[#163321] hover:bg-[#0f2517] text-white rounded-lg disabled:opacity-60" data-testid="btn-save-intermediario">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
